import { AIPlayer } from "../models/ai-player"
import { EnergySource } from "../models/energy-source"
import { Game } from "../models/game"
import { BuyingType } from "@/models/buying-type"

const cooldown = 1000
const sources: EnergySource[] = [
    {
        name: "Coal",
        mwatts: 50,
        price: 100,
        units: 20,
        unitCap: 20,
        imgSrc: "Group 7190.svg",
        id: 0,
        expires: 7,
        pollutionFactor: 10,
        timeToBuild: -1,
        profitMargin: 25,
        profitPerTurn: 1,
        description: "Burning coal produces heat, which can then boil water and drive a steam turbine. Coal is a nonrenewable source, but the United States has large reserves of it. Although it is one of the cheapest ways of generating electricity, burning coal produces more air pollution than other energy sources and contributes to global warming.",
        tips: ["Coal produces energy instantly, which makes it great for reaching your MW requirement right before the round ends.", "Coal doesn't produce revenue, so only buy it if you need to."],
        oldPrice: 100,
        oldUnits: 20
    },
    {
        name: "Wind",
        mwatts: 25,
        price: 150,
        units: 20,
        unitCap: 20,
        imgSrc: "12.svg",
        id: 5,
        expires: 21,
        pollutionFactor: 0,
        timeToBuild: 1,
        profitMargin: 50,
        profitPerTurn: 1,
        description: "When wind blows on a wind turbine, its blades turn, powering an electricity generator. Electricity from wind is cheap, and it produces no pollutants. Wind turbine “farms” require large amounts of land, though, and only windy areas can generate electricity economically. Currently, wind generates electricity in large-scale wind-farms as well as in small backyard operations.",
        tips: ["Wind units produce less energy than most resources of a similar value, however they last longer and produce no emissions. "],
        oldPrice: 150,
        oldUnits: 20
    },
    {
        name: "Geothermal",
        mwatts: 30,
        price: 200,
        units: 20,
        unitCap: 20,
        imgSrc: "Water tank.svg",
        id: 1,
        expires: -1,
        pollutionFactor: 3,
        timeToBuild: 1,
        profitMargin: 20,
        profitPerTurn: 1,
        description: "Geothermal energy is heat energy stored underground in Earth’s crust, in water, rock, or magma. Geothermal energy from water reservoirs is cheap, although there are limited areas where it can be tapped. Other types of geothermal energy are under development.",
        tips: ["Geothermal units lasts forever so they're a great long term investment. "],
        oldPrice: 200,
        oldUnits: 20
    },
    {
        name: "Petroleum",
        mwatts: 40,
        price: 250,
        units: 20,
        unitCap: 20,
        imgSrc: "petroleum no shadow.svg",
        id: 2,
        expires: 7,
        pollutionFactor: 8,
        timeToBuild: 1,
        profitMargin: 80,
        profitPerTurn: 1,
        description: "Petroleum is a naturally occurring liquid found in formations in the Earth consisting of a complex mixture of hydrocarbons. Petroleum is used mostly, by volume, for producing fuel oil and gasoline.",
        tips: ["Petroleum units only lasts for one round, so be sure to replenish them frequently."],
        oldPrice: 250,
        oldUnits: 20
    },
    {
        name: "Biomass",
        mwatts: 35,
        price: 500,
        units: 20,
        unitCap: 20,
        imgSrc: "Bio Mass.svg",
        id: 3,
        expires: 19,
        pollutionFactor: 10,
        timeToBuild: 1,
        profitMargin: 150,
        profitPerTurn: 1,
        description: "Biomass is plant matter that can be burned to produce heat and electricity or converted to liquid and gaseous fuels. Biomass can be organic material from trash and other wastes, or it can be grown specially for energy use. The price of biomass varies widely depending on its nature. Burning biomass produces carbon dioxide, a heat-trapping gas, but if the land used to grow biomass is replanted, the new plants remove equal amounts of carbon dioxide from the atmosphere, resulting in no net contribution to global warming.",
        tips: ["Biomass produces less energy than the resources cheaper than it, however it last longer and generates more revenue. "],
        oldPrice: 500,
        oldUnits: 20
    },
    {
        name: "Natural Gas",
        mwatts: 75,
        price: 650,
        units: 20,
        unitCap: 20,
        imgSrc: "Natural Gas.svg",
        id: 4,
        expires: 7,
        pollutionFactor: 10,
        timeToBuild: 1,
        profitMargin: 200,
        profitPerTurn: 1,
        description: "Burning natural gas is used to power a combustion turbine, similar to those used in jet planes. Domestic natural gas supplies are more limited than coal, making them vulnerable to sudden price increases as demand rises. Natural gas produces the least amount of carbon dioxide and other air pollutants of any fossil fuel when it is burned.",
        tips: ["Petroleum units only lasts for one round, so be sure to replenish them frequently. "],
        oldPrice: 650,
        oldUnits: 20
    },
    {
        name: "Solar",
        mwatts: 100,
        price: 600,
        units: 20,
        unitCap: 20,
        imgSrc: "Group 7493.svg",
        id: 6,
        expires: 37,
        pollutionFactor: 1,
        timeToBuild: 2,
        profitMargin: 100,
        profitPerTurn: 1,
        description: "A photovoltaic, or solar cell converts sunlight directly into electricity, without any polluting by-products. A PV cell is made of semiconductor material. When photons strike a PV cell, they may reflect off the cell, pass through the cell, or be absorbed by the semiconductor material. Only the absorbed photons provide energy to generate electricity. The efficiency at which PV cells convert sunlight to electricity varies by the type of semiconductor material and PV cell technology.",
        tips: ["Solar units produce less energy than most resources of a similar value, however they last longer and produce no emissions."],
        oldPrice: 600,
        oldUnits: 20
    },
    {
        name: "Hydroelectric",
        mwatts: 270,
        price: 1500,
        units: 20,
        unitCap: 20,
        imgSrc: "Group 7029.svg",
        id: 7,
        expires: 25,
        pollutionFactor: 4,
        timeToBuild: 3,
        profitMargin: 250,
        profitPerTurn: 1,
        description: "Hydropower is energy from moving water. In a hydroelectric dam, falling water turns a turbine, creating electricity. Most feasible hydropower sites have already been developed. Building large new dams floods extensive areas, causing social and environmental disruption.",
        tips: ["Hydroelectric units perform well on all metrics. They make a great investment early or late in the game"],
        oldPrice: 1500,
        oldUnits: 20
    },
    {
        name: "Concentrated Solar Thermal",
        mwatts: 400,
        price: 6000,
        units: 20,
        unitCap: 20,
        imgSrc: "Concentrated Solar.svg",
        id: 8,
        expires: -1,
        pollutionFactor: 2,
        timeToBuild: 4,
        profitMargin: 600,
        profitPerTurn: 1,
        description: "Solar thermal power (electricity) generation systems collect and concentrate sunlight to produce the high temperature heat needed to generate electricity. All solar thermal power systems have solar energy collectors with two main components: reflectors that capture and focus sunlight onto a receiver. In most types of systems, a heat-transfer fluid is heated and circulated in the receiver and used to produce steam. The steam is converted into mechanical energy in a turbine, which powers a generator to produce electricity.",
        tips: ["Concentrated Solar units are the most expensive resource in the game but also produce the greatest amount of revenue. If you have the money it's one of the best investments."],
        oldPrice: 6000,
        oldUnits: 20
    },
    {
        name: "Nuclear",
        mwatts: 600,
        price: 4000,
        units: 20,
        unitCap: 20,
        imgSrc: "10.svg",
        id: 9,
        expires: -1,
        pollutionFactor: 4,
        timeToBuild: 7,
        profitMargin: 450,
        profitPerTurn: 1,
        description: "In nuclear fission, atoms are split apart, which releases energy. All nuclear power plants use nuclear fission, and most nuclear power plants use uranium atoms. During nuclear fission, a neutron collides with a uranium atom and splits it, releasing a large amount of energy in the form of heat and radiation. Nuclear energy can also be released in nuclear fusion, where atoms are combined or fused together to form a larger atom. Fusion is the source of energy in the sun and stars.",
        tips: ["Nuclear units take a long time to produce, so plan accordingly."],
        oldPrice: 4000,
        oldUnits: 20
    }
]

export function initialState() {
    return new Game(
        [
            new AIPlayer(1, 0, 1, cooldown, new BuyingType("random")),
            new AIPlayer(2, 0, 69, cooldown, new BuyingType("explicit", [
                sources[4],
                sources[5],
                sources[6],
                sources[7],
                sources[8],
                sources[9],
            ])),
            new AIPlayer(3, 0, 69, cooldown, new BuyingType("largest_availability")),
            new AIPlayer(4, 0, 69, cooldown, new BuyingType("cheapest_availability")),
            new AIPlayer(5, 0, 69, cooldown, new BuyingType("explicit", [
                sources[0],
                sources[3]
            ])),
            new AIPlayer(6, 0, 69, cooldown, new BuyingType("random")),
            new AIPlayer(7, 0, 69, cooldown, new BuyingType("random")),
        ],
        sources,
        3,
        500,
        200,
        7
    )
}
